// frontend/src/pages/login/Login.jsx

import axios from "axios";
import { useState, useContext, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Context } from "../../context/Context";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import "./login.css";
import React from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

export default function Login() {
  const userRef = useRef();
  const navigate = useNavigate();
  const { dispatch, isFetching } = useContext(Context);

  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const isDisabled = isFetching || !email || !password;
  const location = useLocation();
  const [showGooglePrompt, setShowGooglePrompt] = useState(false);

  useEffect(() => {
    if (location.state?.reset) {
      toast.success("Password reset successfully. Please log in.");
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  const generateTempUsername = (name) => {
    const formattedName = name.toLowerCase().replace(/\s/g, "");
    const randomNumber = Math.floor(Math.random() * 9000) + 1000;
    const tempUsername = `${formattedName}${randomNumber}`;

    return tempUsername;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFetching) return;

    // Clear stale data before new login
    localStorage.removeItem("user");
    localStorage.removeItem("token");

    const normalizedEmail = email.trim().toLowerCase();

    if (!email || !password) {
      toast.warn("Please enter both email and password.");
      return;
    }

    dispatch({ type: "LOGIN_START" });

    try {
      const res = await axios.post("/auth/login", {
        email: normalizedEmail,
        password,
      });

      dispatch({ type: "LOGIN_SUCCESS", payload: res.data });
      navigate("/");
    } catch (err) {
      console.log("Login error:", err?.response?.data);

      const code = err?.response?.data?.errorCode;

      switch (code) {
        case "INVALID_CREDENTIALS":
          toast.error("Incorrect email or password.");
          break;
        case "OAUTH_USER":
          toast.error("This account was created with Google. Please use Google login.");
          setShowGooglePrompt(true);
          break;
        case "SERVER_ERROR":
          toast.error("A server error occurred. Please try again.");
          break;
        default:
          toast.error("Login failed. Please try again.");
          break;
      }

      setPassword("");
      dispatch({ type: "LOGIN_FAILURE" });
    }
  };

  const googleLogin = async (credentialResponse) => {
    if (isFetching) return;

    // Clear stale data before new login
    localStorage.removeItem("user");
    localStorage.removeItem("token");

    dispatch({ type: "LOGIN_START" });

    try {
      const { credential } = credentialResponse;
      const decoded = jwtDecode(credential);

      // Extract user info from the decoded JWT token
      const { email, name, picture } = decoded;

      const res = await axios.post("/auth/google", {
        user_name: name,
        username: generateTempUsername(name),
        email,
        profilePic: picture,
      });

      // Flatten payload for consistent reducer handling
      const { user, token } = res.data;
      const flattened = { ...user, token };
      dispatch({ type: "LOGIN_SUCCESS", payload: flattened });
      navigate("/");
    } catch (err) {
      toast.error("Google login failed. Please try again or use email login.");
      dispatch({ type: "LOGIN_FAILURE" });
    }
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }
  }, [rememberMe, email]);

  return (
    <>
    <style>
      {`
        .main-content {
          display: none !important;
        }
      `}
    </style>
    <div className="page-container-login">
      <div className="left-container-login">
        <Link to="/">
          <img
            src={process.env.REACT_APP_AWS_S3_IMAGE_FRONTEND_URL + "/Logo_FLOW_New_Cropped_800x800.png"}
            alt="For Love of Writers Logo"
            className="logo-login"
          />
        </Link>
        <img
          src={process.env.REACT_APP_AWS_S3_IMAGE_FRONTEND_URL + "/bottom_image.jpg"}
          alt="Your Image"
          className="picture"
        />
      </div>
      <div className="right-container-login">
        <form onSubmit={handleSubmit} className="form-login">
          <h1
            style={{
            }}
          >
            Log In
          </h1>
          <div className="input-container-login">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              name="email"
              id="email"
              placeholder="Email Address"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-login"
              autoComplete="username"
              ref={userRef}
              enterKeyHint="next"
            />

            <label htmlFor="password">Password</label>
            <div className="password-container-login">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                id="password"
                placeholder="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-login"
                autoComplete="current-password"
                enterKeyHint="go"
                style={{ paddingRight: "40px" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "15px",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            <div className="remember-me-login" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="input-login"
                  style={{
                    width: "19px",
                    height: "19px",
                    verticalAlign: "middle",
                    lineHeight: "1"
                  }}
                />
                <span style={{ marginLeft: "5px", fontSize: "15px", lineHeight: "1" }}>
                  Remember Me
                </span>
              </label>
              <Link
                to={"/forgot-password"}
                className="linkClass"
              >
                Forgot Password?
              </Link>
            </div>
            <div style={{ justifyContent: "center", paddingTop: "10px" }}>
              <button type="submit" disabled={isDisabled} className="login-btn">
                {isFetching ? "Logging in..." : "Log In"}
              </button>
            </div>
          </div>

          <p>
            Don't have an account?{" "}
            <Link to="/register" style={{ textDecoration: "none" }}>
              {" "}
              Sign Up
            </Link>
          </p>
          <div className="separator-login">
            <hr />
            <span>OR</span>
            <hr />
          </div>
          <div className="button-row">
            {/* <button className="socialButton google">
              <img src={process.env.REACT_APP_AWS_S3_IMAGE_FRONTEND_URL + "/google_logo.png"} alt="Google Logo" />
            </button> */}

            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <div style={{ justifyContent: "center", paddingTop: "10px" }}>
                {showGooglePrompt && (
                  <p role="alert" style={{ color: "#333", fontWeight: "500", marginBottom: "10px" }}>
                    This account uses <strong style={{ color: "#e53935" }}>Google</strong>. Please use the Google login below.
                  </p>
                )}
                <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                  <GoogleLogin
                    shape="rectangular"
                    theme="filled_blue"
                    onSuccess={googleLogin}
                    onError={() => toast.error("Google Sign-In was cancelled or failed.")}
                  />
                </GoogleOAuthProvider>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
